import { ActionReducerMapBuilder, createAsyncThunk } from "@reduxjs/toolkit";
import { graphqlRequest } from "context/graphql/functions";
import localforage from "localforage";
import { JobVisitsStorageKey, JobVisitType } from "models/JobVisitType";
import { createUpdateVisitVars } from "operations/mutations/UpdateVisit";
import {
  ContactType,
  FileType,
  JobStatus,
  MeterReadingInputType,
  PropertyValueInputType,
  TaskStatus,
  TimesType,
  UpdateMachineInputType,
} from "operations/schema/schema";
import { AppAsyncThunkConfig } from "store";
import { State, storeVisitsToLocal, uploadFile } from "./jobs.store";

export const createAppAsyncThunk = createAsyncThunk.withTypes<AppAsyncThunkConfig>();

export const asyncMutations = {
  loadJobVisitsInit: createAppAsyncThunk(
    "jobs/loadJobVisitsInit",
    async (_, { rejectWithValue }) => {
      try {
        const value = await localforage.getItem(JobVisitsStorageKey);
        return { jobVisits: value };
      } catch (err) {
        return rejectWithValue("unable to load in stored job visits");
      }
    },
  ),
  rejectJob: createAppAsyncThunk(
    "jobs/rejectJob",
    async (props: { jobId: string; rejectReason: string }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.rejectJobAssignment, {
        thunkName: "jobs/rejectJob",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.rejectJobAssignment) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  acceptJob: createAppAsyncThunk(
    "jobs/acceptJob",
    async (props: { jobId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { errors, queued } = await graphqlRequest(sdk.updateJobStatus, {
        thunkName: "jobs/acceptJob",
        thunkProps: props,
        variables: {
          jobId,
          status: JobStatus.Accepted,
        },
      });
      if (errors) return rejectWithValue(errors);
      return { queued };
    },
  ),
  downloadJobs: createAppAsyncThunk(
    "jobs/downloadJobs",
    async (props: { jobIds: string[] }, { rejectWithValue, extra: { sdk } }) => {
      const { jobIds } = props;
      const { errors, queued } = await graphqlRequest(sdk.updateJobsStatus, {
        thunkName: "jobs/downloadJobs",
        thunkProps: props,
        variables: {
          jobIds,
          status: JobStatus.Downloaded,
        },
      });
      if (errors) return rejectWithValue(errors);
      return { queued };
    },
  ),

  uploadFile: createAppAsyncThunk(
    "visit/uploadFile",
    async (props: { jobId: string; file: FileType }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId, file } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.uploadFile, {
        thunkName: "visit/uploadFile",
        thunkProps: props,
        variables: {
          jobId,
          file: {
            fileGuid: file.fileGuid,
            name: file.name,
            data: file.data,
            description: file.description,
            fileType: file.fileType,
          },
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.uploadFile) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  //EditMachineLocationDialog
  updateMachineLocation: createAppAsyncThunk(
    "jobs/updateMachineLocation",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineLocation, {
        thunkName: "jobs/updateMachineLocation",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineLocation) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  //EditMachineInstallDateDialog
  updateMachineInstallDate: createAppAsyncThunk(
    "jobs/updateMachineInstallDate",
    async (
      props: { jobId: string; machine: UpdateMachineInputType },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineInstallDate, {
        thunkName: "jobs/updateMachineInstallDate",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineInstallDate) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  //ChangeJobMachineDialog
  changeJobMachine: createAppAsyncThunk(
    "jobs/changeJobMachine",
    async (props: { jobId: string; machineId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.changeJobMachine, {
        thunkName: "jobs/changeJobMachine",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.changeJobMachine) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  // EditContactDialog
  editContact: createAppAsyncThunk(
    "jobs/editContact",
    async (
      props: { jobId: string; customerId: string; machineId: string; contact: ContactType },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.editContact, {
        thunkName: "jobs/editContact",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateContact) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  //EditPlannedDateDialog
  updatePlannedDate: createAppAsyncThunk(
    "jobs/updatePlannedDate",
    async (props: { jobId: string; times: TimesType }, { rejectWithValue, extra: { sdk } }) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updatePlannedDate, {
        thunkName: "jobs/updatePlannedDate",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updatePlannedDate) return rejectWithValue("something went wrong");
      return { queued };
    },
  ),
  // Visit Specific
  completeVisit: createAppAsyncThunk(
    "visit/completeVisit",
    async (props: { jobId: string }, { getState, dispatch, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        jobs: { jobVisits },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      const visit = jobVisits[jobId];
      const completeVisitVars = createUpdateVisitVars(jobId, visit, TaskStatus.Done);

      if (visit.files.length > 0) {
        var failedFiles: FileType[] = [];
        for (const file of visit.files) {
          await dispatch(uploadFile({ jobId, file })).catch(() => {
            failedFiles.push(file);
          });
        }
        if (failedFiles.length > 0) {
          return rejectWithValue({ key: "file-fail", data: failedFiles });
        }
      }

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "visit/completeVisit",
        thunkProps: props,
        variables: completeVisitVars,
        onError: [{ type: "jobs/resetJobStatus", payload: { jobId } }],
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued, followUp: visit.followUp.followUpChecked };
    },
  ),
  updateVisit: createAppAsyncThunk(
    "visit/updateVisit",
    async (props: { jobId: string }, { getState, dispatch, rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const {
        jobs: { jobVisits },
        user: { userVar },
      } = getState();
      if (!userVar) return rejectWithValue("No user");
      const visit = jobVisits[jobId];
      const updateVisitVars = createUpdateVisitVars(jobId, visit, TaskStatus.Accepted);

      if (visit.files.length > 0) {
        var failedFiles: FileType[] = [];
        for (const file of visit.files) {
          await dispatch(uploadFile({ jobId, file })).catch(() => {
            failedFiles.push(file);
          });
        }
        if (failedFiles.length > 0) {
          return rejectWithValue({ key: "file-fail", data: failedFiles });
        }
      }

      const { data, errors, queued } = await graphqlRequest(sdk.updateVisitWithParts, {
        thunkName: "visit/updateVisit",
        thunkProps: props,
        variables: updateVisitVars,
      });

      if (errors) return rejectWithValue(errors);
      if (data && !data.updateVisitWithParts) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  startVisitTravel: createAppAsyncThunk(
    "visit/startVisitTravel",
    async (props: { jobId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateTechStatus, {
        thunkName: "visit/startVisitTravel",
        thunkProps: props,
        variables: { jobId, status: "T" },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateTechStatus) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  startVisitWork: createAppAsyncThunk(
    "visit/startVisitWork",
    async (props: { jobId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateTechStatus, {
        thunkName: "visit/startVisitWork",
        thunkProps: props,
        variables: {
          jobId,
          status: "W",
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateTechStatus) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  pauseTechStatus: createAppAsyncThunk(
    "visit/pauseTechStatus",
    async (props: { jobId: string }, { rejectWithValue, extra: { sdk } }) => {
      const { jobId } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateTechStatus, {
        thunkName: "visit/pauseTechStatus",
        thunkProps: props,
        variables: {
          jobId,
          status: "P",
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateTechStatus) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  updateMachineCustomProps: createAppAsyncThunk(
    "visit/updateMachineCustomProps",
    async (
      props: { jobId: string; properties: PropertyValueInputType[] },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { jobId, properties } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMachineCustomProperties, {
        thunkName: "visit/updateMachineCustomProps",
        thunkProps: props,
        variables: {
          jobId,
          properties,
        },
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMachineCustomProperties)
        return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  //AddChecklistDialog
  addChecklist: createAppAsyncThunk(
    "visit/addChecklist",
    async (
      props: { jobId: string; checklistCode: string },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.addChecklist, {
        thunkName: "visit/addChecklist",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.addChecklist) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
  updateMeters: createAppAsyncThunk(
    "visit/updateMeters",
    async (
      props: { jobId: string; meters: MeterReadingInputType | MeterReadingInputType[] },
      { rejectWithValue, extra: { sdk } },
    ) => {
      const { ...variables } = props;
      const { data, errors, queued } = await graphqlRequest(sdk.updateMeters, {
        thunkName: "visit/updateMeters",
        thunkProps: props,
        variables,
      });
      if (errors) return rejectWithValue(errors);
      if (data && !data.updateMeters) return rejectWithValue("something went wrong");
      return { data, queued };
    },
  ),
};

export const mutationBuilder = (builder: ActionReducerMapBuilder<State>) => {
  builder.addCase(
    asyncMutations.loadJobVisitsInit.fulfilled,
    (state, { payload: { jobVisits } }) => {
      state.jobVisits = Object.assign({}, state.jobVisits, jobVisits);
      const now = new Date().toISOString();
      const result = {} as typeof state.jobVisits;

      for (const key in state.jobVisits) {
        if (!state.jobVisits[key].lastUpdated) {
          state.jobVisits[key].lastUpdated = now;
        }

        const lastUpdate = new Date(state.jobVisits[key].lastUpdated!).valueOf();
        const compare = new Date().valueOf() - 14 * 24 * 60 * 60 * 1000;
        // keep newer than 14 days
        if (lastUpdate >= compare) {
          result[key] = state.jobVisits[key];
        }
      }
      state.jobVisits = result;
      storeVisitsToLocal(state);
      return state;
    },
  );
  builder.addCase(asyncMutations.rejectJob.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.rejectJob.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.rejectJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.rejectJob.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.rejectJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Rejected;
    state.rejectJob.loading = false;
    state.rejectJob.text = "";
    state.rejectJob.open = false;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.jobs[meta.arg.jobId].status = JobStatus.Downloaded;
    return state;
  });
  builder.addCase(asyncMutations.acceptJob.fulfilled, (state, { meta }) => {
    state.jobs[meta.arg.jobId].status = JobStatus.Accepted;
    return state;
  });
  builder.addCase(asyncMutations.downloadJobs.fulfilled, (state, { meta }) => {
    meta.arg.jobIds.forEach((jobId) => {
      state.jobs[jobId].status = JobStatus.Downloaded;
    });
    return state;
  });
  // Upload File
  builder.addCase(asyncMutations.uploadFile.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.fileUploading = true;
    return state;
  });
  builder.addCase(asyncMutations.uploadFile.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.fileUploading = false;
  });
  builder.addCase(asyncMutations.uploadFile.fulfilled, (state) => {
    state.fileUploading = false;
    return state;
  });

  // EditMachineLocationDialog
  builder.addCase(asyncMutations.updateMachineLocation.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineLocation.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.location = machine.location;
    }
    return state;
  });
  // EditMachineInstallDateDialog
  builder.addCase(asyncMutations.updateMachineInstallDate.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineInstallDate.fulfilled, (state, { meta }) => {
    state.editMachineLoading = false;
    const { jobId, machine } = meta.arg;
    if (state.jobs[jobId].machine) {
      state.jobs[jobId].machine!.installDate = machine.installDate;
    }
    return state;
  });
  // ChangeJobMachineDialog
  builder.addCase(asyncMutations.changeJobMachine.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.changeJobMachine.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.changeJobMachine.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.changeJobMachine.fulfilled, (state) => {
    state.changeJobMachine.loading = false;
    return state;
  });
  // EditContactDialog
  builder.addCase(asyncMutations.editContact.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editContact.loading = true;
    return state;
  });
  builder.addCase(asyncMutations.editContact.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editContact.loading = false;
    return state;
  });
  builder.addCase(asyncMutations.editContact.fulfilled, (state, { meta }) => {
    state.editContact.loading = false;
    const { jobId, contact } = meta.arg;
    state.jobs[jobId].preferredContact = {
      email: contact.email,
      firstName: contact.firstName,
      id: contact.id,
      lastName: contact.lastName,
      mobileNumber: contact.mobile,
      name: contact.name,
      phoneNumber: contact.phone,
    };

    return state;
  });
  // Visit Specific
  function processUpdateVisitState(jobVisit: JobVisitType, infoString?: string) {
    // Info string contains completed tasks
    if (infoString) {
      const visitUploaded = infoString.includes("CompleteVisit");
      if (visitUploaded) {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((t) => ({
          ...t,
          uploaded: t.startTime && t.stopTime ? true : false,
          pendingUpload: t.startTime && t.stopTime ? true : false,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          uploaded: true,
          pendingUpload: true,
        }));
        jobVisit.usedParts = [...usedParts];
      } else {
        let extras = jobVisit.extras.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.extras = [...extras];

        let workTimes = jobVisit.workTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.workTimes = [...workTimes];

        let travelTimes = jobVisit.travelTimes.map((e) => ({
          ...e,
          pendingUpload: e.uploaded,
        }));
        jobVisit.travelTimes = [...travelTimes];

        let usedParts = jobVisit.usedParts.map((p) => ({
          ...p,
          pendingUpload: p.uploaded,
        }));
        jobVisit.usedParts = [...usedParts];
      }
    }
    jobVisit.files = [];
    return jobVisit;
  }
  builder.addCase(asyncMutations.completeVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);
    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    if (typeof errors === "object" && "key" in errors) {
      if (errors.key === "file-fail") {
        const files = errors.data as FileType[];
        state.jobVisits[id].files = files;
      }
    } else {
      const { infoString } = ((
        messages: string[],
      ): { shortMessage: string; infoString?: string } => {
        for (let message of messages) {
          if (!message.includes("|")) continue;
          const [s0] = message.split(" at ");
          const [shortMessage, infoString] = s0.split("|");
          return { shortMessage, infoString };
        }
        return {
          shortMessage: messages[0],
        };
      })(errors.map((e) => e.message));
      state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    }
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storeVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.completeVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog,
    );
    return state;
  });

  builder.addCase(asyncMutations.updateVisit.rejected, (state, { payload: errors, meta }) => {
    if (!errors) throw new Error("Something went horribly wrong; error undefined");
    if (typeof errors === "string") throw new Error(errors);

    const id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;

    if (typeof errors === "object" && "key" in errors) {
      if (errors.key === "file-fail") {
        const files = errors.data as FileType[];
        state.jobVisits[id].files = files;
      }
    } else {
      const { infoString } = ((
        messages: string[],
      ): { shortMessage: string; infoString?: string } => {
        for (let message of messages) {
          if (!message.includes("|")) continue;
          const [s0] = message.split(" at ");
          const [shortMessage, infoString] = s0.split("|");
          return { shortMessage, infoString };
        }
        return {
          shortMessage: messages[0],
        };
      })(errors.map((e) => e.message));

      state.jobVisits[id] = processUpdateVisitState(state.jobVisits[id], infoString);
    }
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storeVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.pending, (state, { meta }) => {
    if (meta.queued) return state;
    let id = state.selectedJobId;
    if (!id) throw Error("Missing selectedId");
    state.jobVisits[id].submitLoading = true;
    let extras = state.jobVisits[id].extras.map((e) => ({
      ...e,
      pendingUpload: true,
    }));
    state.jobVisits[id].extras = [...extras];
    let parts = state.jobVisits[id].usedParts.map((p) => ({
      ...p,
      pendingUpload: true,
    }));
    state.jobVisits[id].usedParts = [...parts];
    let workTimes = state.jobVisits[id].workTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].workTimes = [...workTimes];
    let travelTimes = state.jobVisits[id].travelTimes.map((t) => ({
      ...t,
      pendingUpload: t.startTime && t.stopTime ? true : false,
    }));
    state.jobVisits[id].travelTimes = [...travelTimes];
    return state;
  });
  builder.addCase(asyncMutations.updateVisit.fulfilled, (state, { meta, payload: { data } }) => {
    let id = meta.arg.jobId;
    state.jobVisits[id].submitLoading = false;
    state.jobVisits[id] = processUpdateVisitState(
      state.jobVisits[id],
      data?.updateVisitWithParts?.responseLog,
    );
    state.jobVisits[id].lastUpdated = new Date().toISOString();
    storeVisitsToLocal(state);
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.editMachineLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.editMachineLoading = false;
  });
  builder.addCase(asyncMutations.updateMachineCustomProps.fulfilled, (state) => {
    state.editMachineLoading = false;
    return state;
  });
  // AddChecklistDialog
  builder.addCase(asyncMutations.addChecklist.pending, (state, { meta }) => {
    if (meta.queued) return state;
    state.addChecklist.addLoading = true;
    return state;
  });
  builder.addCase(asyncMutations.addChecklist.rejected, (state, { meta }) => {
    if (meta.aborted) return state;
    state.addChecklist.addLoading = false;
  });
  builder.addCase(
    asyncMutations.addChecklist.fulfilled,
    (state, { payload: { data }, meta: { arg } }) => {
      const newChecklist = data?.addChecklist;
      if (newChecklist) {
        const currentChecklists = state.jobVisits[arg.jobId].checklists;
        state.jobVisits[arg.jobId].checklists = [
          ...currentChecklists,
          {
            checklist: newChecklist,
            hasPreStart: newChecklist.questions.some((q) => q.isRequiredPreStart),
          },
        ];
      }
      state.jobVisits[arg.jobId].lastUpdated = new Date().toISOString();
      storeVisitsToLocal(state);

      state.addChecklist.addLoading = false;
      return state;
    },
  );
};
